import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export type BillingTier = 'free' | 'starter' | 'growth' | 'business' | 'enterprise';
export type BillingInterval = 'free' | 'monthly' | 'annual' | 'custom';

export type PlanId =
  | 'free'
  | 'starter_monthly'
  | 'starter_annual'
  | 'growth_monthly'
  | 'growth_annual'
  | 'business_monthly'
  | 'business_annual'
  | 'enterprise';

export type CheckoutPlanId = Exclude<PlanId, 'free' | 'enterprise'>;
export type UsageAction = 'upload' | 'aiQuestion';

export interface UsageLimits {
  uploads: number | null;
  aiQuestions: number | null;
}

export interface PublicPlanConfig {
  id: PlanId;
  tier: BillingTier;
  name: string;
  billingInterval: BillingInterval;
  priceUsdCents: number | null;
  usageLimits: UsageLimits;
  checkoutEnabled: boolean;
  badge: string;
}

export interface UsageSummary {
  monthKey: string;
  planId: PlanId;
  limits: UsageLimits;
  used: {
    uploads: number;
    aiQuestions: number;
  };
  remaining: UsageLimits;
}

export interface BillingCustomer {
  uid: string;
  email: string;
  planId: PlanId;
  effectivePlanId: PlanId;
  planName: string;
  billingInterval: BillingInterval;
  status: string;
  currency: string | null;
  nextPaymentDate: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  paystackCustomerCode: string | null;
  paystackSubscriptionCode: string | null;
}

export interface BillingSummary {
  customer: BillingCustomer;
  usage: UsageSummary;
  plans: PublicPlanConfig[];
}

interface InitializeSubscriptionResponse {
  authorizationUrl: string;
  reference: string;
  checkoutCurrency: string;
  checkoutAmount: number;
}

interface VerifySubscriptionResponse {
  status: 'success';
  alreadyProcessed: boolean;
  billing: BillingSummary;
}

interface CancelSubscriptionResponse {
  status: 'cancel_at_period_end';
  billing: BillingSummary;
}

interface RecordUsageResponse {
  usage: UsageSummary;
}

export const FALLBACK_PLANS: PublicPlanConfig[] = [
  {
    id: 'free',
    tier: 'free',
    name: 'Free',
    billingInterval: 'free',
    priceUsdCents: 0,
    usageLimits: { uploads: 1, aiQuestions: 2 },
    checkoutEnabled: false,
    badge: 'Try GabMic',
  },
  {
    id: 'starter_monthly',
    tier: 'starter',
    name: 'Starter',
    billingInterval: 'monthly',
    priceUsdCents: 2000,
    usageLimits: { uploads: 20, aiQuestions: 100 },
    checkoutEnabled: true,
    badge: 'Small business',
  },
  {
    id: 'starter_annual',
    tier: 'starter',
    name: 'Starter',
    billingInterval: 'annual',
    priceUsdCents: 20000,
    usageLimits: { uploads: 20, aiQuestions: 100 },
    checkoutEnabled: true,
    badge: 'Small business',
  },
  {
    id: 'growth_monthly',
    tier: 'growth',
    name: 'Growth',
    billingInterval: 'monthly',
    priceUsdCents: 2900,
    usageLimits: { uploads: 100, aiQuestions: 500 },
    checkoutEnabled: true,
    badge: 'Growing teams',
  },
  {
    id: 'growth_annual',
    tier: 'growth',
    name: 'Growth',
    billingInterval: 'annual',
    priceUsdCents: 29000,
    usageLimits: { uploads: 100, aiQuestions: 500 },
    checkoutEnabled: true,
    badge: 'Growing teams',
  },
  {
    id: 'business_monthly',
    tier: 'business',
    name: 'Business',
    billingInterval: 'monthly',
    priceUsdCents: 7900,
    usageLimits: { uploads: 500, aiQuestions: 2000 },
    checkoutEnabled: true,
    badge: 'High volume',
  },
  {
    id: 'business_annual',
    tier: 'business',
    name: 'Business',
    billingInterval: 'annual',
    priceUsdCents: 79000,
    usageLimits: { uploads: 500, aiQuestions: 2000 },
    checkoutEnabled: true,
    badge: 'High volume',
  },
  {
    id: 'enterprise',
    tier: 'enterprise',
    name: 'Enterprise',
    billingInterval: 'custom',
    priceUsdCents: null,
    usageLimits: { uploads: null, aiQuestions: null },
    checkoutEnabled: false,
    badge: 'Custom',
  },
];

export const getBillingSummary = async (): Promise<BillingSummary> => {
  const callable = httpsCallable<Record<string, never>, BillingSummary>(functions, 'getBillingSummary');
  const result = await callable({});
  return result.data;
};

export const initializeSubscription = async (planId: CheckoutPlanId): Promise<InitializeSubscriptionResponse> => {
  const callable = httpsCallable<{ planId: CheckoutPlanId }, InitializeSubscriptionResponse>(
    functions,
    'initializePaystackSubscription',
  );
  const result = await callable({ planId });
  return result.data;
};

export const verifySubscription = async (reference: string): Promise<VerifySubscriptionResponse> => {
  const callable = httpsCallable<{ reference: string }, VerifySubscriptionResponse>(functions, 'verifyPaystackTransaction');
  const result = await callable({ reference });
  return result.data;
};

export const cancelSubscription = async (): Promise<CancelSubscriptionResponse> => {
  const callable = httpsCallable<Record<string, never>, CancelSubscriptionResponse>(functions, 'cancelPaystackSubscription');
  const result = await callable({});
  return result.data;
};

export const recordUsage = async (type: UsageAction): Promise<UsageSummary> => {
  const callable = httpsCallable<{ type: UsageAction }, RecordUsageResponse>(functions, 'recordUsage');
  const result = await callable({ type });
  return result.data.usage;
};

export const getPlanById = (plans: PublicPlanConfig[], planId: PlanId) => {
  return plans.find((plan) => plan.id === planId) ?? FALLBACK_PLANS.find((plan) => plan.id === planId) ?? FALLBACK_PLANS[0];
};

export const getPlanForTier = (
  plans: PublicPlanConfig[],
  tier: BillingTier,
  billingInterval: 'monthly' | 'annual',
) => {
  if (tier === 'free') return getPlanById(plans, 'free');
  if (tier === 'enterprise') return getPlanById(plans, 'enterprise');
  return plans.find((plan) => plan.tier === tier && plan.billingInterval === billingInterval)
    ?? FALLBACK_PLANS.find((plan) => plan.tier === tier && plan.billingInterval === billingInterval)
    ?? FALLBACK_PLANS[0];
};

export const formatUsdPrice = (plan: PublicPlanConfig) => {
  if (plan.priceUsdCents === null) return 'Custom';
  if (plan.priceUsdCents === 0) return '$0';
  const amount = plan.priceUsdCents / 100;
  const suffix = plan.billingInterval === 'annual' ? '/year' : '/month';
  return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}${suffix}`;
};

export const formatLimit = (value: number | null, noun: string) => {
  if (value === null) return `Custom ${noun}`;
  return `${value.toLocaleString()} ${noun}`;
};

export const getSubscriptionErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    const message = error.message.trim();
    if (/^(internal|INTERNAL)$/.test(message) || /functions\/internal/i.test(message)) {
      return 'GabMic could not start Paystack checkout. Check the live Paystack setup and try again.';
    }
    return message;
  }
  return 'GabMic billing is unavailable right now.';
};
